import { useCallback, useState } from 'react'
import api from '../../lib/commons/api'
import store from '../../store'
import { addScreenMessage, MessageLevel } from '../../store/messages'
import { intl } from '../../i18n'

// ダウンロード可能なフォーマット。値を正としてここから union 型・拡張子・ラベルIDを導出する。
export const OPENAPI_DOCS_FORMATS = ['html', 'json'] as const
export type OpenApiDocsFormat = (typeof OPENAPI_DOCS_FORMATS)[number]

const OPENAPI_DOCS_DOWNLOAD_PATH = '/api/v1/openapi-docs/download'
// 保存ファイル名は BE の Content-Disposition と揃える（社内名 sevend ではなく対外名 flagxs）
const OPENAPI_DOCS_FILE_BASE_NAME = 'flagxs-external-api'

// API トークン管理画面から OpenAPI ドキュメントをダウンロードする共通処理。
// format(html/json)を指定して取得し、拡張子違いの同名ファイルとして保存する。
// 取得失敗（未生成による 404 等）は握りつぶさず Snackbar でユーザーに通知する（規約 §5）。
// ダウンロード中は isDownloading を true にし、呼び出し側でボタンを無効化して多重実行を防ぐ。
export const useDownloadOpenApiDocs = (functionUuid: string) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = useCallback(
    async (format: OpenApiDocsFormat) => {
      setIsDownloading(true)
      try {
        const blob = await api.functional.requestBlob(
          `${OPENAPI_DOCS_DOWNLOAD_PATH}?format=${format}`
        )
        const url = URL.createObjectURL(blob)
        try {
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = `${OPENAPI_DOCS_FILE_BASE_NAME}.${format}`
          anchor.click()
        } finally {
          URL.revokeObjectURL(url)
        }
      } catch (error) {
        // 障害調査のためエラー内容をログに残しつつ、ユーザーには Snackbar で通知する。
        // どの形式のダウンロードに失敗したかが分かるよう形式名を含める
        console.error('Failed to download OpenAPI docs', error)
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage(
              { id: 'openApiDocs.error.download' },
              { format: format.toUpperCase() }
            ),
          })
        )
      } finally {
        setIsDownloading(false)
      }
    },
    [functionUuid]
  )

  return { handleDownload, isDownloading }
}
